<script setup>
// Painel "📣 ALERTA SIGNL4": liga/desliga o alerta SIGNL4 por contato (ver
// bridge.priority_contact + workflow n8n "‼️ Prioridade - Alerta Crítico
// SIGNL4", documentado em notes/0010 item 18 do SecondBrain). Migrado de
// "Atributos do contato" pra "Ações da conversa" a pedido do Caio em
// 27/09/2026: chave on/off (mesmo padrão visual do IaraToggle) com os
// seletores de escopo/período/categoria aparecendo só quando ligado.
import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';

const SCOPE_VALUES = ['Só do Contato', 'Contato e Grupos'];
const PERIOD_VALUES = [
  'Prioridade 1',
  'Prioridade 24h',
  'Prioridade 7 Dias',
  'Prioridade 1 Mês',
  'Prioridade Sempre',
];
const CATEGORY_VALUES = [
  '🚨 URGENTE',
  '⚠️ IMPORTANTE',
  '✳️ ALERTA',
  '🔹 NOTIFICAÇÃO',
];
const DEFAULT_SCOPE = SCOPE_VALUES[0];
const DEFAULT_PERIOD = 'Prioridade 24h';
const DEFAULT_CATEGORY = CATEGORY_VALUES[0];

const TOGGLE_LABEL = '📣 ALERTA SIGNL4';
const SCOPE_LABEL = 'ALERTA - ESCOPO 🎯';
const PERIOD_LABEL = 'ALERTA - PERÍODO ⏰';
const CATEGORY_LABEL = 'ALERTA - CATEGORIA 📳';

const store = useStore();
const getters = useStoreGetters();

const currentChat = computed(() => getters.getSelectedChat.value);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() =>
  getters['contacts/getContact'].value(contactId.value)
);
const customAttributes = computed(() => contact.value?.custom_attributes || {});

const mode = computed(
  () => customAttributes.value.priority_mode || 'Desativado'
);
const period = computed(() => customAttributes.value.priority_level || '');
const category = computed(() => customAttributes.value.priority_category || '');

const isActive = computed(() => mode.value !== 'Desativado');

const toListOptions = values => values.map((v, i) => ({ id: i + 1, name: v }));
const scopeOptions = toListOptions(SCOPE_VALUES);
const periodOptions = toListOptions(PERIOD_VALUES);
const categoryOptions = toListOptions(CATEGORY_VALUES);

const selectedOf = (values, current) => {
  const id = values.indexOf(current) + 1;
  return id ? { id, name: current } : null;
};
const selectedScope = computed(() => selectedOf(SCOPE_VALUES, mode.value));
const selectedPeriod = computed(() => selectedOf(PERIOD_VALUES, period.value));
const selectedCategory = computed(() =>
  selectedOf(CATEGORY_VALUES, category.value)
);

const persist = async patch => {
  if (!contactId.value) return;
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      customAttributes: { ...customAttributes.value, ...patch },
    });
  } catch (error) {
    useAlert(error?.response?.message || 'Não foi possível salvar o alerta');
  }
};

const toggle = () => {
  if (isActive.value) {
    persist({ priority_mode: 'Desativado' });
  } else {
    persist({
      priority_mode: DEFAULT_SCOPE,
      priority_level: period.value || DEFAULT_PERIOD,
      priority_category: category.value || DEFAULT_CATEGORY,
    });
  }
};

const onSelectScope = ({ name }) => persist({ priority_mode: name });
const onSelectPeriod = ({ name }) => persist({ priority_level: name });
const onSelectCategory = ({ name }) => persist({ priority_category: name });
</script>

<template>
  <div v-if="contactId" class="mb-4">
    <button
      type="button"
      class="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-white font-medium transition-colors"
      :class="
        isActive
          ? 'bg-green-600 hover:bg-green-700'
          : 'bg-red-600 hover:bg-red-700'
      "
      @click="toggle"
    >
      <Icon icon="i-lucide-megaphone" class="size-4" />
      <span>{{ TOGGLE_LABEL }}</span>
      <span class="ml-auto text-xs uppercase">{{
        isActive ? 'On' : 'Off'
      }}</span>
    </button>

    <div v-if="isActive" class="flex flex-col gap-2 mt-2">
      <div>
        <ContactDetailsItem compact :title="SCOPE_LABEL" />
        <MultiselectDropdown
          :options="scopeOptions"
          :selected-item="selectedScope"
          :has-thumbnail="false"
          :multiselector-title="SCOPE_LABEL"
          multiselector-placeholder="Selecionar"
          no-search-result="Nenhum resultado"
          input-placeholder="Buscar"
          @select="onSelectScope"
        />
      </div>
      <div>
        <ContactDetailsItem compact :title="PERIOD_LABEL" />
        <MultiselectDropdown
          :options="periodOptions"
          :selected-item="selectedPeriod"
          :has-thumbnail="false"
          :multiselector-title="PERIOD_LABEL"
          multiselector-placeholder="Selecionar"
          no-search-result="Nenhum resultado"
          input-placeholder="Buscar"
          @select="onSelectPeriod"
        />
      </div>
      <div>
        <ContactDetailsItem compact :title="CATEGORY_LABEL" />
        <MultiselectDropdown
          :options="categoryOptions"
          :selected-item="selectedCategory"
          :has-thumbnail="false"
          :multiselector-title="CATEGORY_LABEL"
          multiselector-placeholder="Selecionar"
          no-search-result="Nenhum resultado"
          input-placeholder="Buscar"
          @select="onSelectCategory"
        />
      </div>
    </div>
  </div>
</template>
